/**
 * What a listing's own text says (backend services/text_facts): facts Jev
 * read from the description, shown only when the reading is sure (a
 * yes/no at 0.8, a choice at Jev's confidence 0.7; owner, 2026-10-29). No
 * percentage is ever shown; an unsure listing simply does not match.
 *
 * The browse URL carries them as the repeatable `fact` key (an alert as
 * `facts`). The filter sheet places each one inside the topic a buyer
 * looks in (documents, seller, condition, rent terms), each with a
 * one-line explainer and one grey "read from the text" note per topic.
 * Cards and listing pages show the positive ones as grey labels.
 */
import type { TranslationKey } from "@/lib/i18n";

export type FactFilter =
  | "docs" | "mortgage" | "no_deed_later" | "owner" | "bright" | "ready" | "needs_work"
  | "no_deposit" | "no_months_ahead";

/**
 * The repair and the mortgage are one question each (2026-10-29): the
 * site's own yes / no (has_repair, has_mortgage) where the text decides
 * when it says something and the tick when it does not (backend
 * services/text_facts). Their old fact values fold into them: a link with
 * `fact=ready` is the repair's "yes".
 */
export const MERGED_FACTS: Record<string, { key: "has_repair" | "has_mortgage"; want: "yes" | "no" }> = {
  ready: { key: "has_repair", want: "yes" },
  needs_work: { key: "has_repair", want: "no" },
  mortgage: { key: "has_mortgage", want: "yes" },
};

/** The facts a URL asks for once the merged questions are counted: their
 *  sides as the facts they stand for, so a card does not repeat them. */
export function askedFacts(sp: { get(k: string): string | null; getAll(k: string): string[] }): string[] {
  const out = new Set(sp.getAll("fact").filter(Boolean));
  if (sp.get("has_repair") === "yes") out.add("ready");
  if (sp.get("has_repair") === "no") out.add("needs_work");
  if (sp.get("has_mortgage") === "yes") out.add("mortgage");
  return [...out];
}

export type FactTopic = "docs" | "seller" | "condition" | "rent_terms";

/** The topics in sheet order, the facts each holds, and the deal they
 *  apply to (rent terms only for rentals, the deed and the papers only
 *  for sales). `switch`: a fact that hides listings, shown as a switch. */
export const FACT_TOPICS: readonly {
  id: FactTopic;
  facts: readonly FactFilter[];
  deal: "sale" | "rent" | "all";
}[] = [
  // "Sənədləri qaydasındadır" is a card label only (owner, 2026-10-29:
  // left off, it read as "no deed"); an old fact=docs link is ignored.
  { id: "docs", facts: ["no_deed_later"], deal: "sale" },
  { id: "seller", facts: ["owner"], deal: "all" },
  { id: "condition", facts: ["bright"], deal: "all" },
  { id: "rent_terms", facts: ["no_deposit", "no_months_ahead"], deal: "rent" },
];

/** Facts that hide listings rather than pick them: a switch, not a pill. */
export const FACT_SWITCHES: ReadonlySet<FactFilter> = new Set(["no_deed_later"]);

export const FACT_FILTERS: readonly FactFilter[] = FACT_TOPICS.flatMap((g) => g.facts);

// Literal keys (the i18n scan finds them per route).
export const FACT_LABEL: Record<FactFilter, TranslationKey> = {
  docs: "listings.fact.docs",
  mortgage: "listings.fact.mortgage",
  no_deed_later: "listings.fact.no_deed_later",
  owner: "listings.fact.owner",
  bright: "listings.fact.bright",
  ready: "listings.fact.ready",
  needs_work: "listings.fact.needs_work",
  no_deposit: "listings.fact.no_deposit",
  no_months_ahead: "listings.fact.no_months_ahead",
};

export const FACT_HINT: Record<FactFilter, TranslationKey> = {
  docs: "listings.fact.docs.hint",
  mortgage: "listings.fact.mortgage.hint",
  no_deed_later: "listings.fact.no_deed_later.hint",
  owner: "listings.fact.owner.hint",
  bright: "listings.fact.bright.hint",
  ready: "listings.fact.ready.hint",
  needs_work: "listings.fact.needs_work.hint",
  no_deposit: "listings.fact.no_deposit.hint",
  no_months_ahead: "listings.fact.no_months_ahead.hint",
};

/** A removable chip's words for a fact (shorter than the switch's). */
export const FACT_CHIP: Record<FactFilter, TranslationKey> = {
  ...FACT_LABEL,
  no_deed_later: "listings.fact.no_deed_later.chip",
};

/** The grey labels a card shows, in the API's order (text_facts). */
export const CARD_LABEL: Record<string, TranslationKey> = {
  owner: "listings.fact.owner",
  docs: "listings.fact.docs",
  mortgage: "listings.fact.mortgage.card",
  ready: "listings.fact.ready",
  needs_work: "listings.fact.needs_work",
  bright: "listings.fact.bright",
  no_deposit: "listings.fact.no_deposit",
};

export const isFactFilter = (v: string): v is FactFilter => (FACT_FILTERS as readonly string[]).includes(v);

/** The topics a sheet shows for the deal it searches ("" sale, "true"
 *  rent, "all" both). */
export function topicsFor(leased: string): typeof FACT_TOPICS[number][] {
  const rent = leased === "true", both = leased === "all";
  return FACT_TOPICS.filter((g) => g.deal === "all" || both || (g.deal === "rent" ? rent : !rent));
}

/** The label ids of a card's text_facts (the deed warning left out). */
export function cardLabels(textFacts: readonly string[] | null | undefined): string[] {
  return (textFacts ?? []).filter((f) => f in CARD_LABEL);
}

/** The text says the deed comes later (the card's amber mark). */
export function deedLaterFromText(textFacts: readonly string[] | null | undefined): boolean {
  return !!textFacts?.includes("deed_later");
}

/** @public (the unit tests exercise it directly)
 * The grey labels a card shows, in order (2026-10-29): from the owner
 *  and the papers ("the deed status"; the deed itself is the icon beside
 *  the price) first, then the facts the search asked for, then the rest
 *  of the listing's text labels; at most `max`. */
export function cardLabelIds(textFacts: readonly string[] | null | undefined, activeFacts: readonly string[], max: number): string[] {
  const have = cardLabels(textFacts);
  const lead = ["owner", "docs"];
  const order = [
    ...lead.filter((f) => have.includes(f)),
    ...have.filter((f) => !lead.includes(f) && activeFacts.includes(f)),
    ...have.filter((f) => !lead.includes(f) && !activeFacts.includes(f)),
  ];
  return order.slice(0, Math.max(0, max));
}
